import { createTheme, type Theme } from '@mui/material/styles';
import type { CSSProperties } from 'react';
import {
  borders,
  colors,
  controlHeight,
  neutral,
  palette,
  red,
  shadows,
  typography,
} from '@ahora-patagonia/design-tokens';

/**
 * Theme del **sitio público** (dirección Zonda), sobre los tokens de `design-tokens`.
 *
 * Comparte el sistema con el panel (Archivo 400/600/800, radio 0, separación por reglas,
 * rampa neutral) con una **divergencia declarada**: acá el rojo es *urgencia editorial*
 * (rótulos, énfasis, links del cuerpo), no estado. Por eso el **foco de teclado se dibuja
 * en tinta** y NO con el `focusRing` rojo de design-tokens — en una página de diario el
 * rojo no puede significar "último momento" y "foco" a la vez.
 *
 * El color del cliente (`Tenant.primaryColor`) no vive acá: llega por dato del loader y se
 * aplica como CSS custom property (`--color-cliente`) en `PublicLayout`, con
 * `--sobre-cliente` para el texto que va encima. Identifica (volanta de categoría,
 * numeración de portada, cabecera y pie) **y** marca la interacción: hover de enlaces, foco,
 * acción primaria y foco de campo. Por eso el theme lo usa por variable y nunca por hex —
 * es único para todos los tenants y el color cambia por request.
 *
 * Modo oscuro: contemplado por diseño y diferido. Cuando entre, los roles son
 * ground `#201E1D` · papel `#2D2B2B` · texto `#F3F2F2` · reglas `#444141` · rojo editorial
 * al paso 400 (`#FF563C`), color del cliente sin cambios, verificado a 4,5:1. La estructura
 * de roles de abajo ya lo admite sin rediseño.
 */

/** Ground alternado de los bloques pares de la home (la "franja" del handoff). */
export const FRANJA = palette.surface;

/** Cortes reales del diseño: 600 y 1200 (los demás breakpoints de MUI quedan disponibles). */
const UP_600 = '@media (min-width:600px)';
const UP_1200 = '@media (min-width:1200px)';

/**
 * Variante tipográfica por corte: 390 es el diseño, 768 y 1440 son ampliaciones.
 * `linea` es line-height relativo (la tabla §6 del handoff).
 */
function escala(
  px390: number,
  px768: number,
  px1440: number,
  peso: number,
  linea: number,
  opts: { heading?: boolean } = {},
): CSSProperties & Record<string, CSSProperties> {
  return {
    fontSize: px390,
    lineHeight: linea,
    fontWeight: peso,
    ...(opts.heading ? { letterSpacing: typography.headingLetterSpacing } : {}),
    [UP_600]: { fontSize: px768 },
    [UP_1200]: { fontSize: px1440 },
  } as CSSProperties & Record<string, CSSProperties>;
}

/** Rótulo en versalita: volanta de categoría, rótulos de bloque. */
const VERSALITA: CSSProperties = {
  fontSize: 11,
  lineHeight: 1.3,
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
};

declare module '@mui/material/styles' {
  interface TypographyVariants {
    titularPortada1: CSSProperties;
    titularPortadaB: CSSProperties;
    titularPortadaC: CSSProperties;
    titularPortada23: CSSProperties;
    titularTarjeta: CSSProperties;
    tituloBloque: CSSProperties;
    volanta: CSSProperties;
    tituloNota: CSSProperties;
    bajada: CSSProperties;
    cuerpoNota: CSSProperties;
    subtituloCuerpo: CSSProperties;
    citaDestacada: CSSProperties;
    firma: CSSProperties;
    epigrafe: CSSProperties;
  }
  interface TypographyVariantsOptions {
    titularPortada1?: CSSProperties;
    titularPortadaB?: CSSProperties;
    titularPortadaC?: CSSProperties;
    titularPortada23?: CSSProperties;
    titularTarjeta?: CSSProperties;
    tituloBloque?: CSSProperties;
    volanta?: CSSProperties;
    tituloNota?: CSSProperties;
    bajada?: CSSProperties;
    cuerpoNota?: CSSProperties;
    subtituloCuerpo?: CSSProperties;
    citaDestacada?: CSSProperties;
    firma?: CSSProperties;
    epigrafe?: CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    titularPortada1: true;
    titularPortadaB: true;
    titularPortadaC: true;
    titularPortada23: true;
    titularTarjeta: true;
    tituloBloque: true;
    volanta: true;
    tituloNota: true;
    bajada: true;
    cuerpoNota: true;
    subtituloCuerpo: true;
    citaDestacada: true;
    firma: true;
    epigrafe: true;
  }
}

export const theme: Theme = createTheme({
  palette: {
    mode: 'light',
    primary: colors.primary, // tinta: la acción primaria es negra, igual que el panel
    secondary: colors.secondary,
    error: colors.error,
    grey: colors.grey,
    background: { default: palette.bg, paper: palette.field },
    text: { primary: palette.ink, secondary: neutral[700], disabled: neutral[500] },
    divider: neutral[300],
  },

  // Cortes de MUI por defecto (600/900/1200/1536); el diseño usa 600 y 1200 como fronteras.

  typography: {
    fontFamily: typography.fontFamily,
    // Tabla §6 del handoff (390 / 768 / 1440 · peso · línea).
    titularPortada1: escala(27, 34, 44, 800, 1.06, { heading: true }),
    // Maqueta B: la nota única toma el frente entero, así que el titular es el más grande del sitio.
    titularPortadaB: escala(31, 44, 62, 800, 1.02, { heading: true }),
    // Maqueta C: las dos del frente comparten cuerpo — son del mismo peso por diseño.
    titularPortadaC: escala(26, 28, 34, 800, 1.08, { heading: true }),
    titularPortada23: escala(19, 21, 22, 800, 1.2, { heading: true }),
    titularTarjeta: escala(17, 18, 19, 800, 1.24, { heading: true }),
    tituloBloque: escala(20, 22, 26, 800, 1.15, { heading: true }),
    volanta: VERSALITA,
    tituloNota: escala(28, 36, 44, 800, 1.1, { heading: true }),
    bajada: escala(17, 19, 19, 400, 1.45),
    cuerpoNota: escala(17, 18, 19, 400, 1.68),
    subtituloCuerpo: escala(21, 23, 26, 800, 1.2, { heading: true }),
    citaDestacada: escala(21, 26, 26, 800, 1.22, { heading: true }),
    firma: escala(12, 12, 13, 400, 1.5),
    epigrafe: escala(13, 13, 13, 400, 1.45),
    body1: escala(15, 15, 16, 400, 1.5),
    body2: escala(13, 13, 13, 400, 1.45),
    button: { fontSize: 14, fontWeight: 800, textTransform: 'none' },
  },

  shape: { borderRadius: 0 },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // Foco de teclado en el color del cliente, nunca rojo: el rojo del sitio es urgencia
        // editorial y no puede además significar foco. Mismo selector `body *` que el panel
        // (gana por especificidad a los resets de MUI sin pelear con el orden de inyección).
        'body *:focus-visible': {
          outline: '2px solid var(--color-cliente)',
          outlineOffset: '2px',
        },
        '::selection': { background: red[200], color: palette.ink },
        body: { backgroundColor: palette.bg, color: palette.ink },
      },
    },

    MuiButtonBase: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: {
          '&:focus-visible': {
            outline: '2px solid var(--color-cliente)',
            outlineOffset: '2px',
          },
        },
      },
    },

    // Estados de links de la superficie de lectura: reposo tinta sin subrayado, hover en el
    // color del cliente con subrayado de 2 px y offset 3. El link del cuerpo de la nota
    // (subrayado permanente de 1 px) lo aplica el renderer, no el theme.
    //
    // El hover lo pide el cliente en su color de marca, no en el rojo editorial del handoff:
    // así la única cosa roja del sitio vuelve a ser la urgencia editorial. Va por
    // `var(--color-cliente)` y no por un hex, porque el theme es único para todos los
    // tenants y el color llega por request (lo publica PublicLayout).
    MuiLink: {
      defaultProps: { underline: 'none', color: 'inherit' },
      styleOverrides: {
        root: {
          color: palette.ink,
          '&:hover': {
            color: 'var(--color-cliente)',
            textDecoration: 'underline',
            textDecorationThickness: '2px',
            textUnderlineOffset: '3px',
          },
          '&:active': { color: 'var(--color-cliente)' },
        },
      },
    },

    // Controles del sitio (búsqueda, "Ver más", compartir): 48 px EN TODOS los cortes —
    // distinto del panel, que baja a 40 en escritorio.
    MuiButton: {
      defaultProps: { disableElevation: true, disableRipple: true },
      styleOverrides: {
        root: {
          minHeight: controlHeight.touch,
          paddingInline: 16,
          gap: 6,
          '&.Mui-disabled': { opacity: 0.45 },
        },
        // La acción primaria va en el color del cliente. El hover se oscurece con `filter`
        // en vez de con un paso de rampa: el color llega como un solo hex por tenant, así
        // que no hay rampa de la que sacar el paso siguiente.
        contained: {
          backgroundColor: 'var(--color-cliente)',
          color: 'var(--sobre-cliente)',
          '&:hover': { backgroundColor: 'var(--color-cliente)', filter: 'brightness(0.88)' },
          '&.Mui-disabled': { backgroundColor: 'var(--color-cliente)', color: 'var(--sobre-cliente)' },
        },
        outlined: {
          borderColor: 'var(--color-cliente)',
          color: 'var(--color-cliente)',
          '&:hover': { backgroundColor: neutral[200], borderColor: 'var(--color-cliente)' },
        },
        text: { color: 'var(--color-cliente)', '&:hover': { backgroundColor: neutral[200] } },
      },
    },

    MuiIconButton: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        // Área táctil de 48 px con el glifo apoyado en el ground, sin caja ni borde.
        root: {
          borderRadius: 0,
          width: controlHeight.touch,
          height: controlHeight.touch,
          color: palette.ink,
          '&:hover': { backgroundColor: neutral[200] },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: palette.field,
          minHeight: controlHeight.touch,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: neutral[500] },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: palette.ink },
          // Foco del campo en el color del cliente (el halo rojo del panel acá no existe).
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--color-cliente)',
            borderWidth: 2,
          },
        },
      },
    },

    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { backgroundImage: 'none' } },
    },

    // La única sombra del sitio: el cajón de navegación.
    MuiDrawer: {
      styleOverrides: {
        paper: { borderRadius: 0, backgroundImage: 'none', boxShadow: shadows.lg },
      },
    },

    MuiSkeleton: {
      defaultProps: { animation: 'wave' },
      styleOverrides: { root: { borderRadius: 0, backgroundColor: neutral[300] } },
    },

    MuiListItemButton: {
      defaultProps: { disableRipple: true },
      styleOverrides: { root: { borderRadius: 0 } },
    },
  },
});

/** Reglas del sistema, re-exportadas para que los componentes no importen design-tokens. */
export const REGLA_ENTRE_BLOQUES = borders.structural;
export const REGLA_ENTRE_NOTAS = borders.row;
